import { z } from 'zod/v4'

import { ReferenceFields } from '../geo'
import { LocationValidator } from './LocationTypes'

export const TargetingFilterValidator = z.record(
	z.union([z.nativeEnum(ReferenceFields), z.string()]),
	z.record(z.literal('percentage'), z.number())
)

export const CoordinateValidator = z.object({
	lat: z.number(),
	lng: z.number(),
	radius: z.number(),
	locationId: LocationValidator.shape.uuid,
})

export const TargetedAreasValidator = z.object({
	counties: z.array(z.string()),
	zipCodes: z.array(z.string()),
	coordinates: z.array(CoordinateValidator),
})

export const TargetingTypeValidator = z.enum(['radius', 'county', 'zip code'])

export const EntityStatusValidator = z.enum(['active', 'archived'])

export const GridValidator = z.object({
	id: z.number(),
	uuid: z.string(),
	name: z.string(),
	created_at: z.iso.datetime(),
	updated_at: z.iso.datetime().nullable(),
	status: EntityStatusValidator,
	auto_update: z.boolean(),
	current_version_id: z.number().nullable(),
	pending_version_id: z.number().nullable(),
	currentVersion: z.lazy(() => GridVersionValidator).optional(),
})

// Base grid for creating new grids
export const BaseGrid = z.object({
	name: z.string(),
	auto_update: z.boolean().default(false),
	current_version_id: z.number().nullable().default(null),
	pending_version_id: z.number().nullable().default(null),
	// Use the same key as hydrated grids, but allow missing server-generated fields.
	currentVersion: z.lazy(() => GridVersionPayload).optional(),
})

// Grid Version
export const GridVersionStatusValidator = z.enum(['active', 'pending', 'archived'])

export const GridVersionValidator = z.object({
	id: z.number(),
	uuid: z.string(),
	grid_id: z.number(),
	name: z.string(),
	version_number: z.number().int(),
	status: GridVersionStatusValidator,
	targeted_areas: TargetedAreasValidator,
	targeting_filters: TargetingFilterValidator,
	manual_inclusion_filters: z.array(z.string()),
	manual_exclusion_filters: z.array(z.string()),
	last_used_at: z.iso.datetime(),
	households: z.number().nullable(),
	targeted_households: z.number().nullable(),
	targeting_type: TargetingTypeValidator.nullable(),
	targeting_coverage: z.number().nullable(),
	created_at: z.iso.datetime(),
	updated_at: z.iso.datetime().nullable(),
})

const GridVersionPayload = GridVersionValidator.omit({
	id: true,
	uuid: true,
	grid_id: true,
	version_number: true,
	created_at: true,
	updated_at: true,
})

export const GridVersionDataSourceValidator = z.object({
	id: z.number(),
	grid_version_id: z.number(),
	data_source_id: z.number(),
	data_source_version_id: z.number(),
})

export type BaseGrid = z.infer<typeof BaseGrid>
export type Grid = z.infer<typeof GridValidator>
export type TargetingFilter = z.infer<typeof TargetingFilterValidator>
export type TargetedAreas = z.infer<typeof TargetedAreasValidator>
export type TargetingType = z.infer<typeof TargetingTypeValidator>
export type Coordinate = z.infer<typeof CoordinateValidator>
export type GridVersion = z.infer<typeof GridVersionValidator>
export type GridVersionStatus = z.infer<typeof GridVersionStatusValidator>
export type GridVersionDataSource = z.infer<typeof GridVersionDataSourceValidator>
export type GridVersionPayload = z.infer<typeof GridVersionPayload>
